/**
 * @copyright 2026 Cooltrax Pty Ltd. All rights reserved.
 */

import type { ReactNode } from 'react'

import { Loader } from '@/components/ui'

export interface DetailSheetProps {
  isOpen: boolean
  /**
   * overlay: absolutely placed over its positioned container from `left`/`top`, sliding in from
   * the right. pane: fills the box it is given (split layouts, where it takes the map's place).
   */
  variant?: 'overlay' | 'pane'
  /** The list's header row, relative to the overlay's positioned container. */
  tableHeader?: { top: number; height: number; left: number }
  /** Where the overlay starts without a measured header. */
  fallbackLeft?: number
  /** The blue title bar's content. Undefined while the item is loading. */
  header?: ReactNode
  /** Use the compact height shared by list/table headers. */
  headerHeight?: 'default' | 'table'
  /** Optional fixed footer below the scrollable body. */
  footer?: ReactNode
  /** Draw the divider above the fixed footer. */
  footerBorder?: boolean
  /** Remounts the body when it changes (a different item), so its scroll starts at the top. */
  bodyKey?: string | number
  children?: ReactNode
}

/**
 * The frame for a list page's detail view. As an overlay it sits where the list's next column would
 * be, starting at its header row with a title bar the same height, so it reads as one more column
 * whose cells span every row. Clicking another item swaps the content in place; the list keeps
 * scrolling underneath.
 */
export const DetailSheet = ({
  isOpen,
  variant = 'overlay',
  tableHeader,
  fallbackLeft = 245,
  header,
  headerHeight = 'default',
  footer,
  footerBorder = true,
  bodyKey,
  children,
}: DetailSheetProps) => {
  const isOverlay = variant === 'overlay'
  const openLeft = tableHeader?.left ?? fallbackLeft

  return (
    <div
      className={
        isOverlay
          ? `absolute right-0 bottom-0 bg-base-100 z-30 flex flex-col border-l border-base-300
  transition-[left] duration-300 ease-in-out`
          : 'h-full w-full bg-base-100 flex flex-col'
      }
      style={isOverlay ? { left: isOpen ? `${openLeft}px` : '100%', top: tableHeader?.top ?? 0 } : undefined}
    >
      {isOpen &&
        (header ? (
          <>
            <div
              className={`bg-primary text-primary-content shrink-0 flex items-center ${
                isOverlay ? 'px-3 text-sm' : headerHeight === 'table' ? 'h-9 px-4 text-sm' : 'py-4 px-2'
              }`}
              style={isOverlay && tableHeader ? { height: tableHeader.height } : undefined}
            >
              {header}
            </div>
            <div key={bodyKey} className="flex-1 min-h-0 overflow-auto p-3">
              {children}
            </div>
            {footer && <div className={`shrink-0 p-4 ${footerBorder ? 'border-t border-base-300' : ''}`}>{footer}</div>}
          </>
        ) : (
          <div className="flex h-full items-center justify-center">
            <Loader />
          </div>
        ))}
    </div>
  )
}
